import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useLocalization } from '../contexts/LocalizationContext';
import { getProfile, updateProfile } from "../services/profileService";
import { changePassword } from "../services/authService";
import LoadingScreen from "./LoadingScreen";
import {
  User,
  Building2,
  Shield,
  Lock,
  Eye,
  EyeOff,
  Save,
  CheckCircle2,
  AlertCircle,
  Mail,
  Phone,
  Globe,
  MapPin,
  FileText,
} from 'lucide-react';

interface Institution {
  institution_code: string;
  institution_name: string;
  email: string;
  telephone: string;
  website: string | null;
  address: string | null;
  sector: string | null;
  legal_status: string | null;
  idnat: string | null;
  nif: string | null;
  rccm: string | null;
}

interface ProfileData {
  id: number;
  username: string;
  firstname: string;
  lastname: string;
  email: string;
  merchant_code: string;
  niveau: number;
  role: string;
  user_status: string;
  status: string;
  institution: Institution;
}

interface ProfileForm {
  username: string;
  firstname: string;
  lastname: string;
  name: string;
  email: string;
  merchant_code: string;
  niveau: number;
  role: string;
  user_status: string;
  status: string;
  institution_code: string;
  company: string;
  phone: string;
  website: string;
  address: string;
  sector: string;
  legal_status: string;
  idnat: string;
  nif: string;
  rccm: string;
  country: string;
  timezone: string;
  language: string;
}

const Profile: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLocalization();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [activeTab, setActiveTab] = useState<'personal' | 'institution' | 'security'>('personal');

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordChanging, setPasswordChanging] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [profile, setProfile] = useState<ProfileForm>({
    username: "",
    firstname: "",
    lastname: "",
    name: "",
    email: "",
    merchant_code: "",
    niveau: 0,
    role: "",
    user_status: "",
    status: "",
    institution_code: "",
    company: "",
    phone: "",
    website: "",
    address: "",
    sector: "",
    legal_status: "",
    idnat: "",
    nif: "",
    rccm: "",
    country: "Ivory Coast",
    timezone: "GMT+0",
    language: "English",
  });

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const data: ProfileData = await getProfile();
        setProfileData(data);

        setProfile({
          username: data.username || "",
          firstname: data.firstname || "",
          lastname: data.lastname || "",
          name: `${data.firstname || ""} ${data.lastname || ""}`.trim(),
          email: data.email || "",
          merchant_code: data.merchant_code || "",
          niveau: data.niveau || 0,
          role: data.role || "Merchant",
          user_status: data.user_status || "",
          status: data.status || "",
          institution_code: data.institution?.institution_code || "",
          company: data.institution?.institution_name || "",
          phone: data.institution?.telephone || "",
          website: data.institution?.website || "",
          address: data.institution?.address || "",
          sector: data.institution?.sector || "",
          legal_status: data.institution?.legal_status || "",
          idnat: data.institution?.idnat || "",
          nif: data.institution?.nif || "",
          rccm: data.institution?.rccm || "",
          country: "Ivory Coast",
          timezone: "GMT+0",
          language: "English",
        });
      } catch (err) {
        console.error("Error loading profile:", err);
      } finally {
        setLoading(false);
      }
    };
    loadProfile();
  }, []);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await updateProfile(profile);
      alert(t('profile.updateSuccess'));
    } catch (err) {
      console.error("Error saving:", err);
      alert(t('profile.updateError'));
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    setPasswordMessage(null);

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordMessage({ type: 'error', text: t('profile.allFieldsRequired') || 'All fields are required' });
      return;
    }

    if (newPassword.length < 6) {
      setPasswordMessage({ type: 'error', text: t('profile.passwordMinLength') || 'Password must be at least 6 characters' });
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordMessage({ type: 'error', text: t('profile.passwordMismatch') || 'Passwords do not match' });
      return;
    }

    setPasswordChanging(true);
    try {
      await changePassword(currentPassword, newPassword);
      setPasswordMessage({ type: 'success', text: t('profile.passwordChanged') || 'Password changed successfully' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      const errorMsg = err?.detail || err?.message || t('profile.passwordChangeError') || 'Error changing password';
      setPasswordMessage({ type: 'error', text: errorMsg });
    } finally {
      setPasswordChanging(false);
    }
  };

  const displayValue = (value: string | null) => {
    return value || t('common.notProvided');
  };

  const tabs = [
    { id: 'personal' as const, label: t('profile.personalInfo') || 'Personal Info', icon: User },
    { id: 'institution' as const, label: t('profile.institutionInfo') || 'Institution', icon: Building2 },
    { id: 'security' as const, label: t('profile.security') || 'Security', icon: Shield },
  ];

  const InputField = ({ label, value, onChange, type = 'text', readOnly = false, icon: Icon }: {
    label: string;
    value: string;
    onChange?: (val: string) => void;
    type?: string;
    readOnly?: boolean;
    icon?: any;
  }) => (
    <div>
      <label className="block text-sm font-medium text-gray-600 mb-1.5">{label}</label>
      <div className="relative">
        {Icon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            <Icon className="h-4 w-4" />
          </div>
        )}
        <input
          type={type}
          value={value}
          onChange={onChange ? (e) => onChange(e.target.value) : undefined}
          readOnly={readOnly}
          className={`
            w-full px-3 py-2.5 rounded-lg border text-sm transition-all
            ${Icon ? 'pl-10' : ''}
            ${readOnly
              ? 'bg-gray-50 border-gray-200 text-gray-500 cursor-not-allowed'
              : 'bg-white border-gray-300 text-gray-900 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500'
            }
          `}
        />
      </div>
    </div>
  );

  if (loading) {
    return <LoadingScreen fullscreen={false} message={t('profile.loading') || 'Chargement...'} />;
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-5 sm:p-6 text-white">
        <div className="absolute inset-0 opacity-[0.12]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.5) 1px, transparent 0)", backgroundSize: "28px 28px" }} />
        <div className="absolute -top-32 -right-20 w-96 h-96 bg-emerald-500/20 rounded-full blur-[100px]" />
        <div className="absolute -bottom-32 -left-20 w-96 h-96 bg-teal-500/15 rounded-full blur-[100px]" />

        <div className="relative flex items-center gap-4">
          <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-emerald-400 to-teal-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-emerald-500/30">
            <span className="text-white font-bold text-xl">
              {profile.firstname ? profile.firstname.charAt(0).toUpperCase() : profile.username.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold tracking-tight">
              {profile.firstname && profile.lastname
                ? `${profile.firstname} ${profile.lastname}`
                : profile.username}
            </h1>
            <p className="text-sm text-slate-400">{profile.company || profile.merchant_code}</p>
            <div className="flex flex-wrap gap-2 mt-2">
              <span className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/20">
                {profile.status || 'Active'}
              </span>
              <span className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-teal-500/15 text-teal-300 border border-teal-500/20">
                {profile.role}
              </span>
              {profile.user_status && (
                <span className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-white/10 text-slate-300 border border-white/10">
                  {profile.user_status}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
        {/* Tab Navigation */}
        <div className="border-b border-gray-100">
          <div className="flex overflow-x-auto">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center gap-2 px-5 py-3.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors
                    ${activeTab === tab.id
                      ? 'border-emerald-600 text-emerald-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Personal Info Tab */}
          {activeTab === 'personal' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <InputField
                  label={t('profile.firstName')}
                  value={profile.firstname}
                  onChange={(val) => setProfile({ ...profile, firstname: val })}
                  icon={User}
                />
                <InputField
                  label={t('profile.lastName')}
                  value={profile.lastname}
                  onChange={(val) => setProfile({ ...profile, lastname: val })}
                  icon={User}
                />
                <InputField
                  label={t('profile.email')}
                  value={profile.email}
                  onChange={(val) => setProfile({ ...profile, email: val })}
                  type="email"
                  icon={Mail}
                />
                <InputField
                  label={t('profile.merchantCode')}
                  value={profile.merchant_code}
                  readOnly
                  icon={FileText}
                />
                <InputField
                  label="Username"
                  value={profile.username}
                  readOnly
                  icon={User}
                />
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                <p className="text-xs text-gray-400">
                  {t('common.lastUpdate')}: {new Date().toLocaleDateString(undefined)}
                </p>
                <button
                  onClick={handleSaveProfile}
                  disabled={saving}
                  className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
                >
                  <Save className="h-4 w-4" />
                  {saving ? t('common.saving') : t('common.saveChanges')}
                </button>
              </div>
            </div>
          )}

          {/* Institution Tab */}
          {activeTab === 'institution' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <InputField
                  label={t('profile.companyName')}
                  value={profile.company}
                  onChange={(val) => setProfile({ ...profile, company: val })}
                  icon={Building2}
                />
                <InputField
                  label={t('profile.institutionCode')}
                  value={profile.institution_code}
                  readOnly
                  icon={FileText}
                />
                <InputField
                  label={t('profile.phone')}
                  value={profile.phone}
                  onChange={(val) => setProfile({ ...profile, phone: val })}
                  icon={Phone}
                />
                <InputField
                  label={t('profile.institutionEmail')}
                  value={profileData?.institution?.email || ""}
                  readOnly
                  icon={Mail}
                />
                <div className="md:col-span-2">
                  <InputField
                    label={t('profile.website')}
                    value={profile.website}
                    onChange={(val) => setProfile({ ...profile, website: val })}
                    icon={Globe}
                  />
                </div>
                <div className="md:col-span-2">
                  <InputField
                    label={t('profile.address')}
                    value={displayValue(profile.address)}
                    onChange={(val) => setProfile({ ...profile, address: val })}
                    icon={MapPin}
                  />
                </div>
              </div>

              {/* Business Details */}
              <div className="pt-4 border-t border-gray-100">
                <h4 className="text-sm font-semibold text-gray-900 mb-4">
                  {t('profile.businessSector') || 'Business Details'}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <InputField
                    label={t('profile.businessSector')}
                    value={displayValue(profile.sector)}
                    onChange={(val) => setProfile({ ...profile, sector: val })}
                  />
                  <InputField
                    label={t('profile.legalStatus')}
                    value={displayValue(profile.legal_status)}
                    onChange={(val) => setProfile({ ...profile, legal_status: val })}
                  />
                  <InputField
                    label="ID NAT"
                    value={displayValue(profile.idnat)}
                    onChange={(val) => setProfile({ ...profile, idnat: val })}
                  />
                  <InputField
                    label="NIF"
                    value={displayValue(profile.nif)}
                    onChange={(val) => setProfile({ ...profile, nif: val })}
                  />
                  <InputField
                    label="RCCM"
                    value={displayValue(profile.rccm)}
                    onChange={(val) => setProfile({ ...profile, rccm: val })}
                  />
                </div>
              </div>

              <div className="flex items-center justify-end pt-4 border-t border-gray-100">
                <button
                  onClick={handleSaveProfile}
                  disabled={saving}
                  className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
                >
                  <Save className="h-4 w-4" />
                  {saving ? t('common.saving') : t('common.saveChanges')}
                </button>
              </div>
            </div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div className="space-y-6">
              {/* Change Password */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Lock className="h-5 w-5 text-gray-700" />
                  <h3 className="text-base font-semibold text-gray-900">
                    {t('profile.changePassword') || 'Change Password'}
                  </h3>
                </div>

                {passwordMessage && (
                  <div className={`flex items-center gap-2 p-3 rounded-lg mb-4 ${
                    passwordMessage.type === 'success'
                      ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
                      : 'bg-red-50 border border-red-200 text-red-700'
                  }`}>
                    {passwordMessage.type === 'success' ? (
                      <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    )}
                    <span className="text-sm">{passwordMessage.text}</span>
                  </div>
                )}

                <div className="max-w-md space-y-4">
                  {/* Current Password */}
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1.5">
                      {t('profile.currentPassword') || 'Current Password'}
                    </label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                        <Lock className="h-4 w-4" />
                      </div>
                      <input
                        type={showCurrentPassword ? 'text' : 'password'}
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        className="w-full pl-10 pr-10 py-2.5 rounded-lg border border-gray-300 text-sm bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                        placeholder="********"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  {/* New Password */}
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1.5">
                      {t('profile.newPassword') || 'New Password'}
                    </label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                        <Lock className="h-4 w-4" />
                      </div>
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="w-full pl-10 pr-10 py-2.5 rounded-lg border border-gray-300 text-sm bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                        placeholder="********"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {newPassword && newPassword.length < 6 && (
                      <p className="text-xs text-amber-600 mt-1">
                        {t('profile.passwordMinLength') || 'Minimum 6 characters'}
                      </p>
                    )}
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1.5">
                      {t('profile.confirmPassword') || 'Confirm New Password'}
                    </label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                        <Lock className="h-4 w-4" />
                      </div>
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full pl-10 pr-10 py-2.5 rounded-lg border border-gray-300 text-sm bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                        placeholder="********"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {confirmPassword && newPassword !== confirmPassword && (
                      <p className="text-xs text-red-600 mt-1">
                        {t('profile.passwordMismatch') || 'Passwords do not match'}
                      </p>
                    )}
                  </div>

                  <button
                    onClick={handlePasswordChange}
                    disabled={passwordChanging || !currentPassword || !newPassword || !confirmPassword || newPassword !== confirmPassword}
                    className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Shield className="h-4 w-4" />
                    {passwordChanging
                      ? (t('common.saving') || 'Saving...')
                      : (t('profile.changePassword') || 'Change Password')}
                  </button>
                </div>
              </div>

              {/* Security Info */}
              <div className="pt-6 border-t border-gray-100">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">
                  {t('profile.accountInfo') || 'Account Info'}
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 border border-gray-100">
                    <div className="h-8 w-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                      <User className="h-4 w-4 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Username</p>
                      <p className="text-sm font-medium text-gray-900">{profile.username}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 border border-gray-100">
                    <div className="h-8 w-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                      <Shield className="h-4 w-4 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">{t('profile.role') || 'Role'}</p>
                      <p className="text-sm font-medium text-gray-900">{profile.role}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 border border-gray-100">
                    <div className="h-8 w-8 rounded-lg bg-amber-50 flex items-center justify-center">
                      <FileText className="h-4 w-4 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">{t('profile.merchantCode')}</p>
                      <p className="text-sm font-medium text-gray-900">{profile.merchant_code}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 border border-gray-100">
                    <div className="h-8 w-8 rounded-lg bg-blue-50 flex items-center justify-center">
                      <CheckCircle2 className="h-4 w-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Status</p>
                      <p className="text-sm font-medium text-gray-900">{profile.status}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
